<template>
  <button
    v-if="bookmarkableType && bookmarkableId"
    :class="['bookmark-button-component', { pulse: showPulse }]"
    :aria-label="buttonLabel"
    @click.stop="handleBookmarkClick"
  >
    <tooltip
      v-if="showTooltip"
      :message="buttonLabel"
      :show-delay="1000"
      :placement="tooltipPosition"
      classes="bookmark-tooltip"
    >
      <icon
        class="bookmark-icon"
        :name="bookmarkIcon"
        :variant="iconVariant"
      />
    </tooltip>
    <icon
      v-else
      class="bookmark-icon"
      :name="bookmarkIcon"
      :variant="iconVariant"
    />
  </button>
</template>

<script>
import Icon from '@components/singletons/icon';
import Tooltip from '@components/singletons/tooltip';
import GlobalMixin from '@/mixins/global';

import { incrementTourSequence } from '@/utils/tour';
import { stripHtml } from '@/utils';

export default {
  components: { Icon, Tooltip },
  mixins: [GlobalMixin],
  props: {
    assetName: {
      type: String,
      default: '',
    },
    bookmarkableType: {
      type: String,
      default: '',
    },
    bookmarkableId: {
      type: Number,
      default: 0,
    },
    contextId: {
      type: Number,
      default: 0,
    },
    iconVariant: {
      type: String,
    },
    showTooltip: {
      type: Boolean,
      default: false,
    },
    source: {
      type: Object,
      default: () => ({}),
    },
    tooltipPosition: {
      type: String,
      default: 'top',
    },
  },
  emits: ['bookmarked'],
  computed: {
    bookmark() {
      return this.$store.getters['bookmarks/get'][`${this.bookmarkableType}-${this.bookmarkableId}`];
    },
    bookmarks() {
      return this.$store.getters['bookmarks/get'];
    },
    bookmarkIcon() {
      if (this.bookmark && !this.bookmark.deleted) {
        return 'bookmark-icon-filled';
      }
      return 'bookmark-icon';
    },
    buttonLabel() {
      if (this.bookmark && !this.bookmark.deleted) {
        return 'Remove Bookmark';
      }

      return 'Add Bookmark';
    },
    showPulse() {
      return this.$store.getters['session/onTour'] && this.$store.getters['session/tourSequence'] === 3;
    },
  },
  methods: {
    createBookmark() {
      const params = {
        bookmarkableId: this.bookmarkableId,
        bookmarkableType: this.bookmarkableType,
        contextId: this.contextId,
        contextType: this.contextId ? 'Category' : null,
        mobileCreatedAt: Date.now(),
        deleted: false,
      };

      this.$store.dispatch('bookmarks/createBookmark', params);
      this.sendAnalytics('bookmarkedAsset');
      this.$emit('bookmarked');
    },
    handleBookmarkClick() {
      if (this.bookmark) {
        this.updateBookmark();
      } else {
        this.createBookmark();
      }
      if (this.$store.getters['session/onTour']) {
        incrementTourSequence.apply(this, [4]);
        this.$store.commit('session/toggleMobileMenu', true);
      }
    },
    sendAnalytics(eventName) {
      const category = this.$store.getters['categories/categories'][this.contextId];
      const analytics = {
        assetId: this.bookmarkableId,
        assetTitle: stripHtml(this.assetName),
        assetType: this.bookmarkableType,
        categoryId: this.contextId,
        categoryName: category?.name,
      };

      this.$analytics.events[eventName](analytics, this.source);
    },
    updateBookmark() {
      const params = {
        id: this.bookmark.id,
        deleted: !this.bookmark.deleted,
      };

      // if the bookmark is currently deleted, we are bookmarking it again. if it's not currently deleted then we must be removing it
      if (this.bookmark.deleted) {
        this.$emit('bookmarked');
      }
      this.sendAnalytics(this.bookmark.deleted ? 'bookmarkedAsset' : 'removedBookmark');

      this.$store.dispatch('bookmarks/updateBookmark', params);
    },
  },
};
</script>
<style lang='scss' scoped>
.bookmark-button-component {
  align-items: center;
  background: transparent;
  border-bottom: 1px solid transparent;
  box-sizing: border-box;
  display: flex;
  flex-shrink: 0;
  height: rem-calc(24);
  padding: 0 rem-calc(4);
  width: rem-calc(24);

  &.pulse {
    border-radius: rem-calc(16);
    animation: blue-pulse 1.5s infinite;
    :deep(img) {
      margin: 0 auto;
    }
  }

  .bookmark-icon {
    margin-top: rem-calc(4);
    height: 100%;
    min-width: rem-calc(24);
  }

  span {
    font-size: rem-calc(14);
    margin-left: rem-calc(8);
  }
}
</style>
